import React, { useState, forwardRef, useImperativeHandle, useMemo } from "react";
import { Button, message, Modal, Space, theme, Typography } from "antd";
const { Text } = Typography;
import PropTypes from "prop-types";
import { formatCurrency, TextForTransactionState, TransactionsType } from "../../utils";
import { currencyGetter } from "../../../../utils/BankCurrencyUtils";
import { edit_bank_transactions } from "../../../../services";

// ! =======================================================================
// ! =======================================================================

export const ModalQuit = forwardRef(({ onConfirm = () => {} }, ref) => {
	const [openAskQuit, setOpenAskQuit] = useState(false);
	
	useImperativeHandle(ref, () => ({
		openModal: () => setOpenAskQuit(true),
	}));

	return (
		<Modal
			title="¿Seguro que quieres salir sin realizar tus cambios?"
			open={openAskQuit}
			onCancel={() => setOpenAskQuit(false)}
			footer={[
				<Button key="cancel" onClick={() => setOpenAskQuit(false)}>
					No, necesito continuar
				</Button>,
				<Button key="save" type="primary" onClick={() => {
					setOpenAskQuit(false);
					onConfirm();
				}}>
					Sí, quiero salir
				</Button>,
			]}
		>
			<Text>Tienes transacciones que están siendo editadas, si sales ahora sin guardar los cambios, vas a perder tu progreso.</Text>
		</Modal>
	);
});

ModalQuit.displayName = "ModalQuit";

ModalQuit.propTypes = {
	onConfirm: PropTypes.func,
};

// ! =======================================================================
// ! =======================================================================

export const ModalTransactionView = forwardRef(({ onConfirm = () => {}, bankAccountSelected = {} }, ref) => {
	const { token : antToken } = theme.useToken();
	const [ selectedTransaction, setSelectedTransaction ] = useState({});
	
	useImperativeHandle(ref, () => ({
		onSetTransaction: (value) => setSelectedTransaction(value),
	}));
	
	return (
		<Modal						
			open={JSON.stringify(selectedTransaction) !== "{}"}
			centered
			width="30vw"
			title="Detalles Transacción"
			onCancel={() => setSelectedTransaction({})}
			footer={() => (
				<Space>
					<Button onClick={() => setSelectedTransaction({})}>Regresar</Button>
					<Button	
						onClick={() => onConfirm(selectedTransaction)}
						type="primary"
						disabled={selectedTransaction?.state !== "unverified"}
					>Verificar</Button>
				</Space>
			)}
		>
			<hr/>
			<p><strong>Número Transacción:</strong> {selectedTransaction?.transaction_number}</p>
			<p><strong>Fecha de Creación:</strong> {selectedTransaction?.date}</p>
			<p><strong>Fecha de Actualización:</strong> {selectedTransaction?.last_update_at}</p>
			<p><strong>Descripción:</strong> {selectedTransaction?.description}</p>

			<hr/>
			<p><strong>Posición:</strong> {selectedTransaction?.position === "in" ? "Venta" : "Compra"}</p>
			<p style={{color: `${selectedTransaction?.position === "in" ? "green" : antToken.colorRed}`}}><strong>Crédito/Débito:</strong> {currencyGetter[bankAccountSelected?.currency]} {formatCurrency(selectedTransaction?.amount)}</p>

			<hr/>
			<p><strong>Estado:</strong> {TextForTransactionState[selectedTransaction?.state]?.label}</p>
			<p><strong>Tipo de Transacción:</strong> {TransactionsType[selectedTransaction?.transaction_type]}</p>
			<p><strong>Nota:</strong> {selectedTransaction?.note ? selectedTransaction?.note : "N/A"}</p>						
			<hr/>
		</Modal>
	);
});

ModalTransactionView.displayName = "ModalTransactionView";

ModalTransactionView.propTypes = {
	onConfirm: PropTypes.func,
	bankAccountSelected: PropTypes.object,
};

// ! =======================================================================
// ! =======================================================================

const TransactionState = Object.freeze({
	ARCHIVED: "archived",
	UNVERIFIED: "unverified",
	VERIFIED: "verified",
});

const ActionToCall = Object.freeze({
	"archive": "Archivar",
	"recover": "Recuperar",
	"delete": "Borrar",
	"verify": "Verificar",
});

const NeededTransactionState = Object.freeze({
	"archive": "No Verificado",
	"recover": "Archivado",
	"delete": "Archivado",
	"verify": "No Verificado",
});

const expectedTransitionState = Object.freeze({
	"archive": TransactionState.UNVERIFIED,
	"recover": TransactionState.ARCHIVED,
	"delete": TransactionState.ARCHIVED,
	"verify": TransactionState.UNVERIFIED,
});

const ActionForTransactions = Object.freeze({
	EMPTY: "empty",
	ARCHIVE: "archive",
	RECOVER: "recover",
	DELETE: "delete",
	VERIFY: "verify",
});

const isValidTheBankTransactionFunction = (transactionCheckData) => {
	const { classification_id = "" , client_id = null, supplier_id = null, note = "" } = transactionCheckData;
	
	if(classification_id === ""){
		return { isValid: false, customMessage: "Porfavor implementa una 'clasificación'" };
	}
	
	if(client_id == null && supplier_id == null){
		const entity = transactionCheckData.position === "in" ? "Cliente" : "Proveedor";			
		return { isValid: false, customMessage: `Porfavor implementa un '${entity}'` };
	}

	if(note === ""){
		return { isValid: false, customMessage: "Porfavor implementa una 'nota'" };
	}
	
	return { isValid: true, customMessage: "" };
};

export const ModalActionSelected = forwardRef(({ 
	token, 
	bankAccountSelected = {},	
	bankTransactionsData = [],
	backupBankTransactionsData = [],
	onUpdateData = () => {},	
	onUpdateBasePoints = () => {},
	targetsSelected = [], 	
}, ref) => {
	const [actionSettings, setActionSettings] = useState({		
		modeSelected: ActionForTransactions.EMPTY,
	});

	const [loading, setLoading] = useState(false);
	
	useImperativeHandle(ref, () => ({		
		getModeSelected: () => actionSettings.modeSelected,
		onSetModeSelected: (modeSelected) => setActionSettings({...actionSettings, modeSelected}),		
	}));

	const actionForEditTransactions = async () => {
		if(actionSettings.modeSelected === ActionForTransactions.EMPTY) return;

		try {			
			setLoading(true);
			const transactions = targetsSelected;
			
			const dataForEditTransactions = {
				bank_account_id: bankAccountSelected.id,
				transactions,
			};

			const res = await edit_bank_transactions(
				token, actionSettings.modeSelected, dataForEditTransactions
			);

			if(res){
				const { 
					message: messageFromRes, 
					failedTransactions, 
					updatedBasePoints = {},
					updatedTemporalBasePoints = {},
				} = res;

				const successfulTransactions = transactions.filter(
					(id) => !failedTransactions.includes(id)
				);
	
				let updatedData = [...bankTransactionsData];
					
				// Update Conforming The Correspondient Scenario
				const actionToDo = actionSettings.modeSelected;
				switch (actionToDo) {
				case ActionForTransactions.VERIFY:
					updatedData = updatedData.map((transaction) =>
						successfulTransactions.includes(transaction.id)
							? { ...transaction, state: TransactionState.VERIFIED}
							: transaction
					);

					// Update the data
					onUpdateData(updatedData);

					// Remove failed transactions from targetsSelected
					setActionSettings({
						modeSelected: ActionForTransactions.EMPTY,
						targetsSelected: targetsSelected.filter(
							(id) => failedTransactions.includes(id)
						),
					});
					break;
					
				case ActionForTransactions.ARCHIVE:					
					updatedData = updatedData.map((transaction) =>
						successfulTransactions.includes(transaction.id)
							? { ...transaction, state: TransactionState.ARCHIVED}
							: transaction
					);

					// Update the data
					onUpdateData(updatedData);

					// Remove failed transactions from targetsSelected
					setActionSettings({
						modeSelected: ActionForTransactions.EMPTY,
						targetsSelected: targetsSelected.filter(
							(id) => failedTransactions.includes(id)
						),
					});
					break;

				case ActionForTransactions.RECOVER:
					updatedData = updatedData.map((transaction) =>
						successfulTransactions.includes(transaction.id)
							? { ...transaction, state: TransactionState.UNVERIFIED }
							: transaction
					);

					// Update the data
					onUpdateData(updatedData);

					// Remove failed transactions from targetsSelected
					setActionSettings({
						modeSelected: ActionForTransactions.EMPTY,
						targetsSelected: targetsSelected.filter(
							(id) => failedTransactions.includes(id)
						),
					});
					break;

				case ActionForTransactions.DELETE:
					// Remove successful transactions from the data
					updatedData = updatedData.filter(
						(transaction) => !successfulTransactions.includes(transaction.id)
					);

					// Update the data
					onUpdateData(updatedData);

					// Remove failed transactions from targetsSelected
					setActionSettings({
						modeSelected: ActionForTransactions.EMPTY,
						targetsSelected: targetsSelected.filter(
							(id) => failedTransactions.includes(id)
						),
					});
					break;
				}
				
				if(actionToDo !== ActionForTransactions.DELETE){
					onUpdateBasePoints(updatedBasePoints, updatedTemporalBasePoints);
				}

				message.success(messageFromRes);
			}
			
		} catch (err) {
			message.error(err.response.data);
		}

		setLoading(false); 
	};
	
	const validStateCount = useMemo(() => {
		return targetsSelected.reduce((count, transactionId) => {
			// Find the transaction in backupBankTransactionsData by ID
			const transaction = backupBankTransactionsData.find(
				(item) => item.id === transactionId
			);

			// Special exeption for verify state:
			if(
				actionSettings.modeSelected === ActionForTransactions.VERIFY && 
				!isValidTheBankTransactionFunction(transaction).isValid
			){
				return count;
			}
	
			// Check if the transaction"s state matches the expected state
			return transaction?.state === expectedTransitionState[actionSettings.modeSelected]
				? count + 1
				: count;
		}, 0);
	}, [targetsSelected, actionSettings.modeSelected]);

	return (
		<Modal
			title={`${ActionToCall[actionSettings.modeSelected]} Transacciones`}
			open={ActionToCall[actionSettings.modeSelected]}
			onCancel={() => setActionSettings({...actionSettings, modeSelected: ActionForTransactions.EMPTY})}
			footer={[
				<Button 
					key="cancel" 
					onClick={() => setActionSettings({...actionSettings, modeSelected: ActionForTransactions.EMPTY})}
					loading={loading}
				>
					Cancelar
				</Button>,
				<Button 
					key="confirm" 
					type="primary" 
					onClick={actionForEditTransactions}
					disabled={validStateCount === 0}
					loading={loading}
				>
					Confirmar
				</Button>,
			]}
		>
			<Text>{"¿Estás seguro que quieres realizar esta acción?"}</Text>
			<br/>
			<br/>
			<Text>{"Recuerda que para realizar esta acción, debes de asegurarte de lo siguiente:"}</Text>
			<br/>
			<br/>
			<Text underline>
				Las transacciones seleccionadas deben de estar en estado <strong>{`"${NeededTransactionState[actionSettings.modeSelected]}"`}</strong>
				{actionSettings.modeSelected === ActionForTransactions.VERIFY && (" y necesitas que cada uno de estos tengan los siguientes campos llenos:")}
				{actionSettings.modeSelected === ActionForTransactions.VERIFY && <ul>
					<li>Clasificación</li>
					<li>Nota</li>
					<li>Cliente/Proveedor</li>
				</ul>}
			</Text>
			
			{validStateCount === 0  && (<>
				<br/>
				<br/>
				<Text type="danger">
					Las transacciones seleccionadas no tienen el estado <strong>{`"${NeededTransactionState[actionSettings.modeSelected]}"`}</strong> {actionSettings.modeSelected === ActionForTransactions.VERIFY && (" y/o los campos solicitados")} para 
					editar, porfavor vuelve a seleccionar las transacciones de la manera esperada.
				</Text>
			</>)}
			{validStateCount > 0 && (
				<>
					<br />
					{actionSettings.modeSelected !== ActionForTransactions.VERIFY && <br />}								
					<Text>
						<strong>{validStateCount}</strong> transacción(es) cumplen con el estado requerido {actionSettings.modeSelected === ActionForTransactions.VERIFY && (" y con los campos requeridos")}.
					</Text>
				</>
			)}
		</Modal>
	);
});

ModalActionSelected.displayName = "ModalActionSelected";

ModalActionSelected.propTypes = {
	token: PropTypes.string.isRequired,
	bankAccountSelected: PropTypes.object,
	bankTransactionsData: PropTypes.array,
	backupBankTransactionsData: PropTypes.array,
	onUpdateData: PropTypes.func,
	onUpdateBasePoints: PropTypes.func,
	targetsSelected: PropTypes.array,
};
